"use client"

import { useEffect, useState } from "react"
import {
  IntroRoot,
  IntroWhite,
  IntroLockup,
  IntroLockupRow,
  IntroTextClip,
  IntroTextGroup,
  IntroWordmark,
  IntroWordmarkAccent,
  IntroTagline,
  IntroIcon,
  type IntroPhase,
} from "./styles"

interface Props {
  /** Brand wordmark text (e.g. "אגד פלוס"). */
  wordmark: string
  /** Brand tagline shown under the wordmark (e.g. "הדרך שלנו לתת יותר"). */
  tagline?: string
  /** Called when the reveal has handed off to the hero (content may appear). */
  onReveal: () => void
  /** Called once the overlay can be removed from the DOM. */
  onFinish: () => void
}

/*
 * EGDE-494: the wordmark carries the real logo colors — the first word
 * ("אגד") stays near-black, the remainder ("פלוס") is brand orange.
 */
function brandWordmark(text: string) {
  const spaceIdx = text.indexOf(" ")
  if (spaceIdx === -1) return text
  return (
    <>
      {text.slice(0, spaceIdx + 1)}
      <IntroWordmarkAccent>{text.slice(spaceIdx + 1)}</IntroWordmarkAccent>
    </>
  )
}

/*
 * EGDE-331: Brand intro reveal. Plays once on first mount.
 *   1. Full-viewport white cover with an aleph-shaped hole punched out — the
 *      real first-slide hero image shows only through the aleph window.
 *   2. The brand lockup (orange icon + wordmark + tagline) writes in below.
 *   3. Brief hold, then the aleph window EXPANDS (the white layer scales up
 *      with its punched hole) until the photo fills the viewport, while the
 *      lockup rides the reveal and fades near its end (EGDE-483 — QA
 *      rejected the earlier in-place opacity dissolve as off-prototype).
 *   4. The hero image sits alone for a beat before onReveal lets the content
 *      (outline, title, CTA, bottom strip) stage in; the header — held back
 *      by a GlobalStyle rule for the splash's whole lifetime — fades in.
 * Honours prefers-reduced-motion, and skips entirely when the page loads
 * mid-scroll (browser scroll restoration) — the splash belongs to a
 * top-of-page load.
 */
/*
 * EGDE-595: the white cover's aleph window is punched out with
 * `mask-image: url("/hero-aleph-cover.svg")` (see IntroWhite). Until that mask
 * has finished loading the cover paints as an unbroken white sheet — on a slow
 * connection the splash ran its fixed timeline against a blank white screen and
 * the window only appeared once the mask landed, part-way through the reveal.
 *
 * Resolve once the mask has actually decoded so the timeline starts against a
 * cover that already has its window. Capped so a failed or blocked request can
 * never strand the visitor behind the overlay — after the cap we start anyway
 * and accept the degraded frame, which is what happens today.
 */
/*
 * EGDE-740 (r2): IntroWhite serves a second, low-DPR twin of the mask where a
 * device pixel is a CSS pixel (see its note, and hero-aleph-cover-lowdpr.svg).
 * The gate below has to wait on the file this visitor will actually paint —
 * decoding the other one releases the timeline against a cover whose window has
 * not landed, which is precisely the EGDE-595 bug. The query is the same string
 * as the stylesheet's so the two can only ever agree.
 */
const MASK_DPR_QUERY =
  "(max-resolution: 1.5dppx), (-webkit-max-device-pixel-ratio: 1.5)"
const MASK_SRC = "/hero-aleph-cover.svg"
const MASK_SRC_LOW_DPR = "/hero-aleph-cover-lowdpr.svg"
const maskSrc = () =>
  typeof window !== "undefined" && window.matchMedia(MASK_DPR_QUERY).matches
    ? MASK_SRC_LOW_DPR
    : MASK_SRC
const MASK_WAIT_CAP_MS = 1200

/**
 * "ready"   — the mask decoded; the composite is complete, play the splash.
 * "slow"    — still not ready at the cap; play anyway (no worse than before,
 *             and the visitor is never held behind the overlay).
 * "failed"  — the request errored; the cover can never punch its window, so a
 *             splash would just be a blank white screen. Skip it.
 */
type MaskState = "ready" | "slow" | "failed"

/*
 * EGDE-494 (r4): `onDecoded` fires when the mask is genuinely on screen, which
 * is NOT the same moment the returned promise settles. The promise is capped at
 * MASK_WAIT_CAP_MS so a slow request can never strand the visitor behind the
 * overlay, and past that cap it resolves "slow" with the mask still in flight.
 * Releasing the lockup on that would put the entrance back in front of a blank
 * cover, which is the whole bug: the sheet is 84KB, so on a real slow
 * connection it lands well after the 1.2s cap. The timeline keeps the cap; the
 * lockup waits for the picture.
 */
function waitForMask(onDecoded: () => void): Promise<MaskState> {
  if (typeof window === "undefined") return Promise.resolve("slow")
  return new Promise<MaskState>((resolve) => {
    let settled = false
    const settle = (state: MaskState) => {
      if (settled) return
      settled = true
      resolve(state)
    }
    const cap = setTimeout(() => settle("slow"), MASK_WAIT_CAP_MS)
    let announced = false
    const decoded = () => {
      // onload and decode() both land here; only the first one counts.
      if (announced) return
      announced = true
      onDecoded()
    }
    const finish = (state: MaskState) => {
      clearTimeout(cap)
      if (state === "ready") decoded()
      settle(state)
    }

    const img = new window.Image()
    img.onerror = () => finish("failed")
    img.onload = () => finish("ready")
    img.src = maskSrc()
    // decode() resolves only once the bitmap is ready to paint; onload alone can
    // still leave a frame where the mask is not yet usable.
    if (typeof img.decode === "function") {
      img.decode().then(
        () => finish("ready"),
        // A rejected decode() on an image that did load is not fatal — onload
        // still settles this promise.
        () => {},
      )
    }
  })
}

export default function HeroIntro({
  wordmark,
  tagline,
  onReveal,
  onFinish,
}: Props) {
  const [phase, setPhase] = useState<IntroPhase>("reveal")
  // EGDE-494 (r4): the lockup's CSS entrance is held paused until the mask has
  // resolved, so it plays against a cover that already has its window instead of
  // burning its second behind a blank white sheet. See LOCKUP_PLAY_STATE.
  const [lockupReady, setLockupReady] = useState(false)

  // EGDE-318: mark the document while the splash plays so viewport-fixed
  // chrome from OTHER blocks (the HeroStrip notification banner, z-1002)
  // can hold back — it would otherwise show through the transparent aleph
  // window (the splash's z-2000 white has a hole punched in it). The strip
  // listens for the finish event and only then appears.
  useEffect(() => {
    document.documentElement.setAttribute("data-hero-intro", "")
    return () => {
      document.documentElement.removeAttribute("data-hero-intro")
      window.dispatchEvent(new Event("hero-intro-finished"))
    }
  }, [])

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    const midScroll = typeof window !== "undefined" && window.scrollY > 100
    /*
     * EGDE-719: a URL fragment is a request for one block, not a top-of-page
     * load — and the splash's own contract, two comments up, is that it belongs
     * to a top-of-page load. It used to honour that by accident: the browser's
     * hash jump was instant, so by the time this effect ran `scrollY` was already
     * thousands of pixels down and `midScroll` skipped the splash. Now that the
     * landing is animated it starts from zero, and whether the check catches it
     * is a race against hydration — measured on the homepage, three loads of
     * /#FaqAnchor out of four played the full five-second brand screen over a
     * page that was scrolling underneath it. Read the fragment directly.
     */
    const anchorEntry =
      typeof window !== "undefined" && window.location.hash.length > 1
    // EGDE-458: the splash is a first-impression moment — navigating away and
    // back (or to any other page with a hero) must not replay it. Session
    // storage marks it as played for the rest of the tab's session.
    let played = false
    try {
      played = sessionStorage.getItem("heroIntroPlayed") === "true"
    } catch {
      // sessionStorage unavailable — treat as not played
    }

    if (reduce || midScroll || anchorEntry || played) {
      // EGDE-460: stamp the document so the GlobalStyle rule hides this (and
      // any later) overlay mount instantly instead of flashing for a frame.
      document.documentElement.setAttribute("data-intro-played", "")
      onReveal()
      onFinish()
      return
    }

    try {
      sessionStorage.setItem("heroIntroPlayed", "true")
    } catch {
      // sessionStorage unavailable
    }

    // EGDE-460 / EGDE-457: lock page scroll while the splash plays. Scrolling
    // mid-splash slid arbitrary page content (and the header chrome) through
    // the fixed overlay's transparent aleph window, visually shredding the
    // animation.
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    // EGDE-595: hold the timeline until the aleph mask can actually paint, so
    // the lockup never writes in over a blank white cover and the window is
    // already punched out when the expansion starts.
    let timers: ReturnType<typeof setTimeout>[] = []
    let cancelled = false

    void waitForMask(() => {
      // Fires on the real decode, which past the cap is LATER than the promise
      // below. That is the point: the entrance may only start once the cover
      // actually has its window.
      if (!cancelled) setLockupReady(true)
    }).then((maskState) => {
      if (cancelled) return

      // EGDE-595: without its mask the cover is an opaque white rectangle, so a
      // splash would show nothing but white for ~5s. Hand straight over to the
      // hero instead — the same path a repeat visit takes.
      if (maskState === "failed") {
        document.documentElement.setAttribute("data-intro-played", "")
        document.body.style.overflow = prevOverflow
        onReveal()
        onFinish()
        return
      }

      // EGDE-460: QA asked for a less hurried splash — the lockup holds for an
      // extra 600ms before the reveal. EGDE-483: the reveal is now the aleph
      // window EXPANDING over 1.5s (prototype), not a 1s in-place dissolve.
      timers = [
        setTimeout(() => setPhase("expand"), 3200), // aleph window expands (2.2s)
        setTimeout(() => {
          // Expansion done — image stands alone; the hero content's own staged
          // delays (0.2s / 0.7s / 1.1s) provide the media-only pause from the
          // video before the outline, text and strip appear.
          setPhase("done")
          onReveal()
        }, 5400),
        setTimeout(() => {
          // EGDE-460: mark the splash as played for CSS (see GlobalStyle) so a
          // client-side remount of the overlay never paints again.
          document.documentElement.setAttribute("data-intro-played", "")
          // EGDE-483: only a REAL splash run stamps this — it drives the
          // header's slide-in-from-above entrance (GlobalStyle). Skip paths
          // (repeat visit / reduced motion / mid-scroll) leave the header
          // static.
          document.documentElement.setAttribute("data-intro-finished", "")
          onFinish()
        }, 5600),
      ]
    })

    return () => {
      cancelled = true
      timers.forEach(clearTimeout)
      document.body.style.overflow = prevOverflow
    }
  }, [onReveal, onFinish])

  return (
    <IntroRoot $phase={phase} aria-hidden="true" data-hero-intro>
      <IntroWhite $phase={phase} />
      {wordmark && (
        <IntroLockup $phase={phase} $ready={lockupReady}>
          <IntroLockupRow>
            {/* Icon first in source → inline-start (right in RTL). It fades in
                alone on the lockup's centre, then glides to its seat while the
                text slides out from behind it — see the transform trio above
                IntroLockupRow in styles.ts. */}
            <IntroIcon />
            <IntroTextClip>
              <IntroTextGroup>
                <IntroWordmark>{brandWordmark(wordmark)}</IntroWordmark>
                {tagline && <IntroTagline>{tagline}</IntroTagline>}
              </IntroTextGroup>
            </IntroTextClip>
          </IntroLockupRow>
        </IntroLockup>
      )}
    </IntroRoot>
  )
}
